import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { CreditCard, Plus, Trash2, FileUp, Loader2, Eye } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import CardProfileDialog, { SourceChip, useProfile } from "./CardProfile";
import { readB64, errMsg } from "./util";

function CardRow({ c, onRemove, onView, idx }) {
  const [p] = useProfile({ issuer: c.issuer, cardName: c.card_name });
  return (
    <div className="flex items-center gap-3 rounded-xl border border-stone-200 bg-white px-3 py-2.5" data-testid={`cl-existing-${idx}`}>
      <div className="w-10 h-7 rounded-md bg-gradient-to-br from-[#0B4D3A] to-[#14684F] grid place-items-center shrink-0"><CreditCard className="w-4 h-4 text-[#D4AF37]" /></div>
      <div className="min-w-0 flex-1">
        <div className="font-bold text-sm truncate">{c.issuer} · {c.card_name}</div>
        <div className="flex items-center gap-1.5 mt-0.5"><span className="text-[10px] text-stone-400 uppercase font-bold">{c.source === "customer360" ? "Customer 360" : c.source}</span><SourceChip p={p} /></div>
      </div>
      <button onClick={onView} className="p-1.5 rounded-full hover:bg-stone-100" data-testid={`cl-existing-view-${idx}`}><Eye className="w-4 h-4 text-stone-500" /></button>
      <button onClick={onRemove} className="p-1.5 rounded-full hover:bg-red-50" data-testid={`cl-existing-remove-${idx}`}><Trash2 className="w-4 h-4 text-stone-400" /></button>
    </div>
  );
}

export default function ExistingCards({ session, onSave, onSession, beforeUpload }) {
  const cards = session.existing_cards || [];
  const [banks, setBanks] = useState([]);
  const [issuer, setIssuer] = useState("");
  const [other, setOther] = useState(false);
  const [name, setName] = useState("");
  const [suggest, setSuggest] = useState([]);
  const [view, setView] = useState(null);
  const [pwd, setPwd] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();

  useEffect(() => { api.get("/hid/meta").then((r) => setBanks((r.data.banks || []).filter((b) => b !== "Other"))).catch(() => {}); }, []);
  useEffect(() => { if (issuer) api.get("/hid/cards", { params: { bank: issuer, kind: "credit" } }).then((r) => setSuggest(r.data.cards || [])).catch(() => setSuggest([])); }, [issuer]);

  const add = () => {
    if (!issuer.trim() || !name.trim()) return toast.error("Pick the bank and type the card name");
    onSave([...cards, { issuer: issuer.trim(), card_name: name.trim(), source: "manual" }]);
    setName("");
  };
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      if (beforeUpload) await beforeUpload();
      const { data } = await api.post(`/card-lens/sessions/${session.id}/statement`, { filename: file.name, data_b64: await readB64(file), password: pwd || null });
      onSession(data.session);
      toast.success(`Statement read — ${data.statement.months_covered || 1} month(s) of spends added`);
    } catch (e) { toast.error(errMsg(e, "Couldn't read statement")); }
    setBusy(false);
  };

  return (
    <div className="space-y-3" data-testid="cl-existing-cards">
      {cards.length === 0 && <div className="text-sm text-stone-500 rounded-xl border border-dashed border-stone-300 p-4" data-testid="cl-no-cards">No cards yet. Add the cards they hold, or skip this if they don't have one. We'll suggest cards from their habits.</div>}
      {cards.map((c, i) => <CardRow key={`${c.issuer}-${c.card_name}`} idx={i} c={c} onView={() => setView(c)} onRemove={() => onSave(cards.filter((_, j) => j !== i))} />)}
      <div className="flex flex-wrap gap-2">
        {other ? (
          <Input value={issuer} onChange={(e) => setIssuer(e.target.value)} placeholder="Type bank / issuer" className="h-10 rounded-full w-48" data-testid="cl-add-issuer-other" />
        ) : (
          <select value={issuer} onChange={(e) => { if (e.target.value === "__other") { setOther(true); setIssuer(""); } else setIssuer(e.target.value); }} className="h-10 border border-stone-200 rounded-full px-3 text-sm bg-white" data-testid="cl-add-issuer">
            <option value="">Bank / issuer</option>
            {banks.map((b) => <option key={b} value={b}>{b}</option>)}
            <option value="__other">Other (type it)</option>
          </select>
        )}
        <Input list="cl-card-suggest" value={name} onChange={(e) => setName(e.target.value)} placeholder="Card name, e.g. Millennia" className="h-10 rounded-full flex-1 min-w-[180px]" data-testid="cl-add-name" />
        <datalist id="cl-card-suggest">{suggest.map((s) => <option key={s} value={s} />)}</datalist>
        <Button onClick={add} variant="outline" className="rounded-full h-10 border-[#0B4D3A] text-[#0B4D3A] font-bold" data-testid="cl-add-card"><Plus className="w-4 h-4 mr-1" /> Add card</Button>
      </div>
      <div className="rounded-xl bg-[#FBF8F1] p-3 flex flex-wrap items-center gap-2" data-testid="cl-statement-box">
        <FileUp className="w-4 h-4 text-[#0B4D3A]" />
        <div className="text-xs text-stone-600 flex-1 min-w-[200px]"><b>Customer unsure of spends?</b> Upload a credit-card statement PDF. We read spend per category and the card itself.</div>
        <Input value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder="PDF password (if any)" className="h-9 rounded-full w-44 text-xs" data-testid="cl-statement-password" />
        <input ref={fileRef} type="file" accept="application/pdf" hidden onChange={(e) => { upload(e.target.files[0]); e.target.value = ""; }} data-testid="cl-statement-input" />
        <Button size="sm" onClick={() => fileRef.current?.click()} disabled={busy} className="rounded-full bg-[#0B4D3A] hover:bg-[#083B2D]" data-testid="cl-statement-upload">{busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <FileUp className="w-3.5 h-3.5 mr-1" />}{busy ? "Reading…" : "Upload statement"}</Button>
      </div>
      {(session.statements || []).length > 0 && <div className="text-[11px] text-stone-500" data-testid="cl-statements">{session.statements.map((s) => `${s.filename}${s.issuer ? ` (${s.issuer} ${s.card_name || ""})` : ""}`).join(" · ")}</div>}
      {view && <CardProfileDialog open onClose={() => setView(null)} issuer={view.issuer} cardName={view.card_name} />}
    </div>
  );
}
